<script setup lang="ts">
import { computed, onMounted } from 'vue';
import mobileDetectStore from '@src/application/store/mobile-detect-store.ts';

type SpreadChildrenExpansionModeType = 'minimal'|'maximal';

type Gap = '0'|'1'|'2'|'3'|'4'|'5';

const props = withDefaults(defineProps<{

    /* Abstand zwischen den Elementen */
    gap?: Gap

    /* bei wrap=true dürfen die Elemente bei zu wenig Platz umbrechen auf die nächte Zeile */
    wrap?: boolean

    /* Elemente stehen auch auf kleinen Bildschirmen nebeneinander / Standardmäßig platzieren sich Elemente auf kleinen Bildschirmen untereinander */
    wrapOnSmallScreen?: boolean

    /* Abstand zwischen den Elemente auf kleinen Bildschirmen */
    gapOnSmallScreen?: Gap

    /* bringt Elemente auf die gleiche Höhe */
    growChildren?: boolean|'small-screen'|'large-screen'

    /* zentriert Elemente in der Höhe */
    centerChildren?: boolean|'small-screen'|'large-screen'

    /* ordnet alle Kinder am unteren Rand an */
    bottomAlignChildren?: boolean

    /* lässt den Wrapper auf maximale Breite wachsen */
    spreadSelf?: boolean

    /* lässt alle Kind-Elemente in der Breite wachsen */
    spreadChildren?: boolean|'small-screen'|'large-screen'

    /*
       wird nur ausgewertet, wenn spread-children ungleich false ist
       minimal - Elemente wachsen und teilen sich den zur Verfügung stehenden Platz im Verhältnis ihrer benötigten Breite
       maximal - Elemente wachsen und teilen den Platz der Zeile zu gleichen Teilen (nur wenn wrap=false)
   */
    spreadChildrenExpansionMode?: SpreadChildrenExpansionModeType

    /* vertikale Ausrichtung der Elemente innerhalb des Wrappers */
    alignment?: 'left'|'center'|'right'

    /* Trennlinie zwischen den Elementen */
    separator?: boolean

}>(), {
    gap: '0',
    wrapOnSmallScreen: true,
});

const spreadChildrenExpansion = computed<false|SpreadChildrenExpansionModeType>(() => props.spreadChildren !== false ? props.spreadChildrenExpansionMode !== undefined ? props.spreadChildrenExpansionMode : 'minimal' : false);

const isMobile = computed(() => mobileDetectStore.isMobile.data);

onMounted(() => {
    if (props.growChildren && props.centerChildren) {
        console.error('grow-children und center-children können nicht beide gesetzt werden');
    }
    if (props.spreadChildrenExpansionMode !== undefined && props.spreadChildren === false) {
        console.error('spread-children-expansion-mode hat keine Auswirkung, solange spread-children nicht gesetzt ist');
    }
});
</script>

<template>
    <div
        :class="[
            $style.horizontal_align_children_wrapper,
            gap !== '0' || gapOnSmallScreen !== '0' ? [$style.gap, (isMobile && gapOnSmallScreen !== undefined) ? $style['gap_' + gapOnSmallScreen] : $style['gap_' + gap]] : '',
            separator && (isMobile && gapOnSmallScreen !== '0' || !isMobile && gap !== '0') ? $style.separator : '',
            wrap ? $style.wrap : '',
            wrapOnSmallScreen ? $style.wrap_on_small_screen : '',
            spreadSelf ? $style.spread_self : '',
            growChildren ? (growChildren === true ? $style.grow : $style['grow_' + growChildren]) : '',
            centerChildren ? (centerChildren === true ? $style.center : $style['center_' + centerChildren]) : '',
            spreadChildren ? (spreadChildren === true ? $style.spread : $style['spread_' + spreadChildren]) : '',
            spreadChildrenExpansion ? $style[spreadChildrenExpansion] : '',
            bottomAlignChildren ? $style.align_bottom : '',
            alignment ? $style['align_' + alignment] : '',
        ]"
    >
        <slot/>
    </div>
</template>

<style lang="scss" module>
@use '@borders' as *;

.horizontal_align_children_wrapper {
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    &.spread_self {
        width: 100%;
    }
    &.grow {
        align-items: stretch;
    }
    &.center {
        align-items: center;
    }
    &.align_bottom {
        align-items: flex-end;
    }
    &.spread {
        &.minimal > * {
            flex: 1 1 auto;
        }
        &.maximal > * {
            flex: 1 1 100%;
        }
    }
    &.align_left {
        justify-content: flex-start;
    }
    &.align_center {
        justify-content: center;
    }
    &.align_right {
        justify-content: flex-end;
    }

    @media (max-width: 32.99em) {
        &.grow_small-screen {
            align-items: stretch;
        }
        &.center_small-screen {
            align-items: center;
        }
        &.spread_small-screen {
            &.minimal > * {
                flex: 1 1 auto;
            }
            &.maximal > * {
                flex: 1 1 100%;
            }
        }
    }

    @media (min-width: 33em) {
        &.grow_large-screen {
            align-items: stretch;
        }
        &.center_large-screen {
            align-items: center;
        }
        &.spread_large-screen {
            &.minimal > * {
                flex: 1 1 auto;
            }
            &.maximal > * {
                flex: 1 1 100%;
            }
        }
    }
}

.wrap {
    flex-wrap: wrap;
}

@media (max-width: 32.99em) {
    .wrap_on_small_screen {
        flex-direction: column;
        align-items: stretch;
    }
}

.gap {
    --gap1: .3em;
    --gap2: .7em;
    --gap3: 1.5em;
    --gap4: 2.5em;
    --gap5: 4em;

    @media (min-width: 33em) {
        --gap1: .5em;
        --gap2: 1em;
        --gap3: 2em;
        --gap4: 3em;
        --gap5: 5em;
    }
}

.gap_1 {
    gap: var(--gap1);

    &.separator > * + *::before {
        left: calc(((var(--gap1) / 2) + 1px) * -1);
    }
    &.wrap_on_small_screen {
        &.separator > * + *::before {
            @media (max-width: 32.99em) {
                top: calc(((var(--gap1) / 2) - 1px) * -1);
                left: 0;
            }

            @media (min-width: 33em) {
                left: calc(((var(--gap1) / 2) + 1px) * -1);
            }
        }
    }
}
.gap_2 {
    gap: var(--gap2);

    &.separator > * + *::before {
        left: calc(((var(--gap2) / 2) + 1px) * -1);
    }
    &.wrap_on_small_screen {
        &.separator > * + *::before {
            @media (max-width: 32.99em) {
                top: calc(((var(--gap2) / 2) - 1px) * -1);
                left: 0;
            }

            @media (min-width: 33em) {
                left: calc(((var(--gap2) / 2) + 1px) * -1);
            }
        }
    }
}
.gap_3 {
    gap: var(--gap3);

    &.separator > * + *::before {
        left: calc(((var(--gap3) / 2) + 1px) * -1);
    }
    &.wrap_on_small_screen {
        &.separator > * + *::before {
            @media (max-width: 32.99em) {
                top: calc(((var(--gap3) / 2) - 1px) * -1);
                left: 0;
            }

            @media (min-width: 33em) {
                left: calc(((var(--gap3) / 2) + 1px) * -1);
            }
        }
    }
}
.gap_4 {
    gap: var(--gap4);

    &.separator > * + *::before {
        left: calc(((var(--gap4) / 2) + 1px) * -1);
    }
    &.wrap_on_small_screen {
        &.separator > * + *::before {
            @media (max-width: 32.99em) {
                top: calc(((var(--gap4) / 2) - 1px) * -1);
                left: 0;
            }

            @media (min-width: 33em) {
                left: calc(((var(--gap4) / 2) + 1px) * -1);
            }
        }
    }
}
.gap_5  {
    gap: var(--gap5);

    &.separator > * + *::before {
        left: calc(((var(--gap5) / 2) + 1px) * -1);
    }
    &.wrap_on_small_screen {
        &.separator > * + *::before {
            @media (max-width: 32.99em) {
                top: calc(((var(--gap5) / 2) - 1px) * -1);
                left: 0;
            }

            @media (min-width: 33em) {
                left: calc(((var(--gap5) / 2) + 1px) * -1);
            }
        }
    }
}

.separator {
    > * {
        position: relative;

        + *::before {
            content: '';
            position: absolute;
            background-color: #989898;
            width: 1px;
            height: 100%;
        }
    }
    &.wrap_on_small_screen > * + *::before {
        width: 100%;
        height: 1px;

        @media (min-width: 33em) {
            width: 1px;
            height: 100%;
        }
    }
}
</style>
